import React, { ReactNode, ReactNodeArray, useCallback, useMemo, useRef, useState } from 'react';
import { MBIconV2Name } from '@shared/components/image';
import { TOAST_DEFAULT_DURATION } from '@shared/styles';
import { createCtx } from '@shared/util/context';
import * as uuid from '@shared/util/uuid';
import { MBToast } from './toast';

interface Props {
  children?: ReactNode;
}

export interface ToastAction {
  onPress?(): void;
  label?: string;
  element?: ReactNode;
}
export interface ToastOptions {
  style?: 'confirmation' | 'warning' | 'default' | 'modal';
  layout?: 'short' | 'long';
  /**
   * Duration (ms) after which the toast will automatically dismiss
   */
  duration?: number;
  delay?: number;
  icon?: MBIconV2Name;
  actions?: ToastAction[];
  message: string;
  prioritize?: boolean;
  showClose?: boolean;
}

export interface ToastAPI {
  show(options: ToastOptions): void;
  clearAll(): void;
}

export type ToastState = 'pending' | 'active' | 'anim-out';
interface ToastInstance {
  options: ToastOptions;
  state: ToastState;
  timeoutHandle?: number;
  uuid: string;
}

export const [useToast, ToastContextProvider, ToastContext] = createCtx<ToastAPI>();

function applyDefaults(options: ToastOptions): ToastOptions {
  return {
    ...options,
    duration: typeof options.duration === 'number' ? options.duration : TOAST_DEFAULT_DURATION,
    layout: options.layout || 'short',
    style: options.style || 'default',
  };
}

function useToastQueue(): {
  toasts: ReactNodeArray;
  createToast(options: ToastOptions): void;
  clear(): void;
} {
  const [renderQueue, setRenderQueue] = useState<ToastInstance[]>([]);
  const queue = useRef<ToastInstance[]>([]);

  const process = useRef(() => {
    if (queue.current.length > 0 && queue.current[0].state === 'pending') {
      // Show the first pending toast in the queue, this starts the lifecycle sequence
      queue.current[0].state = 'active';
    }
    setRenderQueue([...queue.current]);
  }).current;

  const updateToastState = useRef((toastId: string, state: ToastState) => {
    for (let i = 0; i < queue.current.length; i++) {
      if (queue.current[i].uuid === toastId) {
        queue.current[i].state = state;
        break;
      }
    }
    process();
  }).current;

  const removeToast = useRef((toastId: string) => {
    for (let i = 0; i < queue.current.length; i++) {
      if (queue.current[i].uuid === toastId) {
        queue.current.splice(i, 1);
        break;
      }
    }
    process();
  }).current;

  const createToast = useRef((options: ToastOptions) => {
    const newToast: ToastInstance = {
      options: applyDefaults(options),
      state: 'pending',
      uuid: uuid.v4(),
    };
    if (options.prioritize) {
      queue.current.forEach((_, i) => {
        queue.current[i].state = 'pending';
      });
      queue.current.unshift(newToast);
    } else {
      queue.current.push(newToast);
    }
    process();
  }).current;

  const toasts = useMemo<ReactNodeArray>(
    () =>
      renderQueue
        .filter(toast => toast.state !== 'pending')
        .map(toast => (
          <MBToast
            key={toast.uuid}
            options={toast.options}
            onDismiss={() => updateToastState(toast.uuid, 'anim-out')}
            state={toast.state}
            onExit={() => removeToast(toast.uuid)}
          />
        )),
    [renderQueue, updateToastState, removeToast],
  );

  const clear = useCallback(() => setRenderQueue([]), []);

  return {
    toasts,
    createToast,
    clear,
  };
}

export function MBToastProvider({ children }: Props) {
  const messageToasts = useToastQueue();
  const actionToasts = useToastQueue();
  const modalToasts = useToastQueue();

  const show = useCallback<ToastAPI['show']>(
    (options: ToastOptions) => {
      if (options.style === 'confirmation' || options.style === 'warning') {
        messageToasts.createToast(options);
      } else if (options.style === 'modal') {
        modalToasts.createToast(options);
      } else {
        actionToasts.createToast(options);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageToasts.createToast, modalToasts.createToast, actionToasts.createToast],
  );

  const value = useMemo<ToastAPI>(
    () => ({
      show,
      clearAll: () => {
        messageToasts.clear();
        actionToasts.clear();
        modalToasts.clear();
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [show, messageToasts.clear, actionToasts.clear, modalToasts.clear],
  );

  return (
    <ToastContextProvider value={value}>
      {children}
      {!modalToasts.toasts?.length && messageToasts.toasts}
      {!modalToasts.toasts?.length && actionToasts.toasts}
      {modalToasts.toasts}
    </ToastContextProvider>
  );
}
